import { FC, useState, useEffect, useRef } from 'react'
import { useSelector } from 'react-redux'
import { useQuery, useMutation } from '@apollo/client'
import { useHistory } from 'react-router'
import {
  ErrorMessage,
  Field,
  Form,
  Formik,
  FormikHelpers,
  FormikProps
} from 'formik'
import * as Yup from 'yup'
import { Button } from 'react-bootstrap'

import { useAppDispatch, useAppSelector } from 'hooks/store'
import useLoadingDelay from 'hooks/useLoadingDelay'
import { loadingSpinnerPortal } from 'components/atoms/LoadingSpinner'
import Select from 'components/features/common/select/Select'
import {
  shouldNavigateToVerifySpouseInformation,
  shouldNavigateToDisclosureAgreement,
  shouldNavigateToCourseInstructions,
  mainCoursePagePath
} from 'share/utils/courseUtils'
import { CourseOrderItem, OrderCustomerDetails } from 'share/domain/course'
import { ActiveCase, isDebtorType1 } from 'reducers/activecase/activecaseSlice'
import {
  ActiveCourse,
  setVerifiedDetailsTrue
} from 'reducers/activecourse/activecourseSlice'
import {
  GET_ORDER_PRIMARY_APPLICANT_CUSTOMER_DETAILS,
  GetOrderPrimaryApplicantCustomerDetailsVariables,
  GetOrderPrimaryApplicantCustomerDetailsResponse
} from 'share/queries/courses'
import {
  UPDATE_ORDER_PRIMARY_APPLICANT_CUSTOMER_DETAILS,
  UpdateOrderPrimaryApplicantCustomerDetailsVariables,
  UpdateOrderPrimaryApplicantCustomerDetailsResponse,
  UPDATE_ORDER_VERIFIED_CUSTOMER_DETAILS_FLAG,
  UpdateOrderVerifiedCustomerDetailsFlagVariables,
  UpdateOrderVerifiedCustomerDetailsFlagResponse
} from 'share/mutations/courses'
import { dropdownStateOptions, phoneRegExp } from 'share/utils/utils'
import {
  VerifyInfoFieldsRow,
  VerifyInfoFieldsWrapper
} from 'components/atoms/InputFields/VerifyInfo'
import { TwoBtnsContainer } from 'components/atoms/Layout'

const userId = '(User ID)'
const emailText = 'Email Address'
const mobileText = 'Mobile Phone'

interface CoursesVerifyPrimaryFormProps {
  activeCase: ActiveCase
  activeCourse: ActiveCourse
  course: CourseOrderItem
}

interface VerifyPrimaryFormValues {
  city: string
  emailAddress: string
  firstName: string
  homePhone: string
  lastName: string
  mailingAddress: string
  middleName: string
  mobilePhone: string
  selectState: string
  suffix: string
  workPhone: string
  zipCode: string
}

function setInitialValues(
  customerDetails: OrderCustomerDetails
): VerifyPrimaryFormValues {
  const initialValues: VerifyPrimaryFormValues = {
    city: customerDetails?.city ?? '',
    emailAddress: customerDetails?.emailAddress ?? '',
    firstName: customerDetails?.firstName ?? '',
    homePhone: customerDetails?.homePhone ?? '',
    lastName: customerDetails?.lastName ?? '',
    mailingAddress: customerDetails?.street ?? '',
    middleName: customerDetails?.middleName ?? '',
    mobilePhone: customerDetails?.mobilePhone ?? '',
    selectState: customerDetails?.state ?? '',
    suffix: customerDetails?.suffix ?? '',
    workPhone: customerDetails?.workPhone ?? '',
    zipCode: customerDetails?.zip ?? ''
  }
  return initialValues
}

function getEmptyInitialValues(): VerifyPrimaryFormValues {
  const initialValues: VerifyPrimaryFormValues = {
    city: '',
    emailAddress: '',
    firstName: '',
    homePhone: '',
    lastName: '',
    mailingAddress: '',
    middleName: '',
    mobilePhone: '',
    selectState: '',
    suffix: '',
    workPhone: '',
    zipCode: ''
  }
  return initialValues
}

const validationSchema = Yup.object({
  firstName: Yup.string().required('First Name is Required'),
  lastName: Yup.string().required('Last Name is Required'),
  middleName: Yup.string().max(1),
  suffix: Yup.string(),
  homePhone: Yup.string().matches(
    phoneRegExp,
    'Home phone number is not valid'
  ),
  mobilePhone: Yup.string().matches(
    phoneRegExp,
    'Mobile phone number is not valid'
  ),
  workPhone: Yup.string().matches(
    phoneRegExp,
    'Work phone number is not valid'
  ),
  emailAddress: Yup.string().email('Invalid email format').required('Required'),
  mailingAddress: Yup.string(),
  zipCode: Yup.string(),
  city: Yup.string(),
  selectState: Yup.string().required('State selection required')
})

const CoursesVerifyPrimaryForm: FC<CoursesVerifyPrimaryFormProps> = ({
  activeCase,
  activeCourse,
  course
}) => {
  const isDebtor1 = useSelector(isDebtorType1)
  const history = useHistory()
  const dispatch = useAppDispatch()
  const authentication = useAppSelector((state) => state.authentication)
  const [loaded, setLoaded] = useState(false)
  const [submitErrorMessage, setSubmitErrorMessage] =
    useState<string | null>(null)

  const formRef = useRef<FormikProps<VerifyPrimaryFormValues>>(null)

  const { data, loading, error } = useQuery<
    GetOrderPrimaryApplicantCustomerDetailsResponse,
    GetOrderPrimaryApplicantCustomerDetailsVariables
  >(GET_ORDER_PRIMARY_APPLICANT_CUSTOMER_DETAILS, {
    variables: {
      request: {
        orderId: course.orderId,
        caseId:
          activeCase.matter && activeCase.matter.id ? activeCase?.matter.id : 0
      }
    }
  })

  const [updatePrimaryCustomerDetails, { loading: updateLoading }] =
    useMutation<
      UpdateOrderPrimaryApplicantCustomerDetailsResponse,
      UpdateOrderPrimaryApplicantCustomerDetailsVariables
    >(UPDATE_ORDER_PRIMARY_APPLICANT_CUSTOMER_DETAILS)

  const [
    updatePrimaryCustomerVerifiedDetailsFlag,
    { loading: updateVerifiedLoading }
  ] = useMutation<
    UpdateOrderVerifiedCustomerDetailsFlagResponse,
    UpdateOrderVerifiedCustomerDetailsFlagVariables
  >(UPDATE_ORDER_VERIFIED_CUSTOMER_DETAILS_FLAG)

  useEffect(() => {
    if (
      data &&
      data.orders &&
      data.orders.orderPrimaryApplicantCustomerDetails
    ) {
      const initial = setInitialValues(
        data.orders.orderPrimaryApplicantCustomerDetails
      )

      formRef.current?.setValues(initial, true)
      setLoaded(true)
    }

    if (error) {
      const emptyInitial = getEmptyInitialValues()
      formRef.current?.setValues(emptyInitial, true)
      setLoaded(true)
    }
  }, [data, error])

  async function updatePrimaryCustomerDetailsHelper(
    formValues: VerifyPrimaryFormValues
  ): Promise<boolean> {
    let result = false
    try {
      const input: OrderCustomerDetails = {
        caseId:
          activeCase.matter && activeCase.matter.id ? activeCase?.matter.id : 0,
        orderId: data?.orders.orderPrimaryApplicantCustomerDetails.orderId
          ? data?.orders.orderPrimaryApplicantCustomerDetails.orderId
          : 0,
        orderKey: data?.orders.orderPrimaryApplicantCustomerDetails.orderKey
          ? data?.orders.orderPrimaryApplicantCustomerDetails.orderKey
          : 0,
        personId: data?.orders.orderPrimaryApplicantCustomerDetails.personId
          ? data?.orders.orderPrimaryApplicantCustomerDetails.personId
          : 0,
        firstName: formValues.firstName,
        lastName: formValues.lastName,
        middleName: formValues.middleName,
        suffix: formValues.suffix,
        homePhone: formValues.homePhone,
        mobilePhone: formValues.mobilePhone,
        workPhone: formValues.workPhone,
        emailAddress: formValues.emailAddress,
        street: formValues.mailingAddress,
        city: formValues.city,
        state: formValues.selectState,
        zip: formValues.zipCode
      }

      const updateResult = await updatePrimaryCustomerDetails({
        variables: { primaryDetails: input }
      })
      if (
        updateResult.data?.orders.updateOrderPrimaryApplicantCustomerDetails
          .success
      ) {
        result = true
      }
    } catch (e) {
      result = false
    }

    if (!result) {
      setSubmitErrorMessage('Error saving the primary customer details')
    }

    return result
  }

  async function updatePrimaryCustomerVerifiedDetailsFlagHelper(): Promise<boolean> {
    let result = false
    try {
      const input = {
        flag: true,
        orderId: data?.orders.orderPrimaryApplicantCustomerDetails.orderId
          ? data.orders.orderPrimaryApplicantCustomerDetails.orderId
          : 0,
        caseId:
          activeCase.matter && activeCase.matter.id ? activeCase?.matter.id : 0
      }

      const updateResult = await updatePrimaryCustomerVerifiedDetailsFlag({
        variables: { request: input }
      })
      if (
        updateResult.data?.orders.updateOrderVerifiedCustomerDetailsFlag.success
      ) {
        result = true
        dispatch(setVerifiedDetailsTrue(course.courseNumber))
      }
    } catch (e) {
      result = false
    }

    return result
  }

  function onSubmitWrapper(
    formik: FormikProps<VerifyPrimaryFormValues>,
    e: React.FormEvent<HTMLFormElement>
  ) {
    e.preventDefault()
    // only want to save data if something has changed
    if (formik.dirty) {
      formik.handleSubmit(e)
    } else {
      handleNavigationToNextPage()
    }
  }

  async function handleNavigationToNextPage() {
    // update verified details flag before navigating to the next page
    await updatePrimaryCustomerVerifiedDetailsFlagHelper()
    let path: string | null = null
    if (
      (path = shouldNavigateToVerifySpouseInformation(
        isDebtor1,
        course,
        activeCase,
        activeCourse
      ))
    ) {
      history.push(path)
    } else if ((path = shouldNavigateToDisclosureAgreement(course))) {
      history.push(path)
    } else if ((path = shouldNavigateToCourseInstructions(course))) {
      history.push(path)
    } else {
      history.push(mainCoursePagePath())
    }
  }

  async function handleBackButtonClick() {
    history.push({
      pathname: '/dashboard/courses'
    })
  }

  async function onSubmit(
    values: VerifyPrimaryFormValues,
    { setSubmitting }: FormikHelpers<VerifyPrimaryFormValues>
  ): Promise<void> {
    const updateResult = await updatePrimaryCustomerDetailsHelper(values)
    setSubmitting(false)
    if (updateResult) {
      handleNavigationToNextPage()
    }
  }

  const loadings = loading || !loaded || updateLoading || updateVerifiedLoading

  const loadingDelay = useLoadingDelay(loadings)

  if (error) {
    return <div>{error?.message}</div>
  }

  const emailUserId =
    authentication.account?.userName !== authentication.account?.emailAddress
      ? emailText
      : `${emailText} ${userId}`

  const mobileUserId =
    authentication.account?.userName !== authentication.account?.mobilePhone
      ? mobileText
      : `${mobileText} ${userId}`

  return (
    <>
      {loadingDelay && loadingSpinnerPortal()}
      <Formik
        initialValues={validationSchema.getDefault() as VerifyPrimaryFormValues}
        innerRef={formRef}
        validationSchema={validationSchema}
        onSubmit={onSubmit}
      >
        {(formik) => (
          <Form onSubmit={(e) => onSubmitWrapper(formik, e)}>
            <VerifyInfoFieldsWrapper
              id='primaryCustomerDetails'
              data-testid='course-verify-information-form'
            >
              <VerifyInfoFieldsRow>
                <div className='form-group'>
                  <label htmlFor='firstName'>First Name</label>
                  <Field
                    autoFocus
                    className={
                      formik.errors.firstName
                        ? 'form-control is-invalid'
                        : 'form-control'
                    }
                    id='firstName'
                    name='firstName'
                    data-testid='course-verify-information-first-name-field'
                    placeholder=''
                  />
                  <ErrorMessage
                    name='firstName'
                    render={(msg) => (
                      <span
                        className='text-danger'
                        data-testid='course-verify-information-first-name-field-error'
                      >
                        {msg}
                      </span>
                    )}
                  />
                </div>
                <div className='form-group'>
                  <label htmlFor='lastName'>Last Name</label>
                  <Field
                    className={
                      formik.errors.lastName
                        ? 'form-control is-invalid'
                        : 'form-control'
                    }
                    id='lastName'
                    name='lastName'
                    data-testid='course-verify-information-last-name-field'
                    placeholder=''
                    type='text'
                  />
                  <ErrorMessage
                    name='lastName'
                    render={(msg) => (
                      <span
                        className='text-danger'
                        data-testid='course-verify-information-last-name-field-error'
                      >
                        {msg}
                      </span>
                    )}
                  />
                </div>
              </VerifyInfoFieldsRow>

              <VerifyInfoFieldsRow>
                <div className='form-group'>
                  <label htmlFor='middleName'>Middle Initial</label>
                  <Field
                    className='form-control'
                    id='middleName'
                    maxLength={1}
                    name='middleName'
                    data-testid='course-verify-information-middle-name-field'
                    placeholder='Optional'
                    type='text'
                  />
                  <ErrorMessage
                    name='middleName'
                    render={(msg) => (
                      <span
                        className='text-danger'
                        data-testid='course-verify-information-middle-name-field-error'
                      >
                        {msg}
                      </span>
                    )}
                  />
                </div>
                <div className='form-group'>
                  <label htmlFor='suffix'>Suffix</label>
                  <Field
                    className='form-control'
                    id='suffix'
                    name='suffix'
                    data-testid='course-verify-information-suffix-field'
                    placeholder='Optional'
                    type='text'
                  />
                  <ErrorMessage
                    name='suffix'
                    render={(msg) => (
                      <span
                        className='text-danger'
                        data-testid='course-verify-information-suffix-field-error'
                      >
                        {msg}
                      </span>
                    )}
                  />
                </div>
              </VerifyInfoFieldsRow>

              <VerifyInfoFieldsRow>
                <div className='form-group'>
                  <label htmlFor='homePhone'>Home Phone</label>
                  <Field
                    className={
                      formik.errors.homePhone
                        ? 'form-control is-invalid'
                        : 'form-control'
                    }
                    id='homePhone'
                    name='homePhone'
                    data-testid='course-verify-information-home-phone-field'
                    placeholder=''
                    type='text'
                  />
                  <ErrorMessage
                    name='homePhone'
                    render={(msg) => (
                      <span
                        className='text-danger'
                        data-testid='course-verify-information-home-phone-field-error'
                      >
                        {msg}
                      </span>
                    )}
                  />
                </div>

                <div className='form-group'>
                  <label htmlFor='mobilePhone'>{mobileUserId}</label>
                  <Field
                    readOnly
                    className={
                      formik.errors.mobilePhone
                        ? 'form-control is-invalid'
                        : 'form-control'
                    }
                    id='mobilePhone'
                    name='mobilePhone'
                    data-testid='course-verify-information-mobile-phone-field'
                    placeholder='Optional'
                    type='text'
                  />
                  <ErrorMessage
                    name='mobilePhone'
                    render={(msg) => (
                      <span
                        className='text-danger'
                        data-testid='course-verify-information-mobile-phone-field-error'
                      >
                        {msg}
                      </span>
                    )}
                  />
                </div>
                <div className='form-group'>
                  <label htmlFor='workPhone'>Work Phone</label>
                  <Field
                    className={
                      formik.errors.workPhone
                        ? 'form-control is-invalid'
                        : 'form-control'
                    }
                    id='workPhone'
                    name='workPhone'
                    data-testid='course-verify-information-work-phone-field'
                    placeholder='Optional'
                    type='text'
                  />
                  <ErrorMessage
                    name='workPhone'
                    render={(msg) => (
                      <span
                        className='text-danger'
                        data-testid='course-verify-information-work-phone-field-error'
                      >
                        {msg}
                      </span>
                    )}
                  />
                </div>
              </VerifyInfoFieldsRow>

              <VerifyInfoFieldsRow>
                <div className='form-group'>
                  <label htmlFor='emailAddress'>{emailUserId}</label>
                  <Field
                    readOnly
                    className={
                      formik.errors.emailAddress
                        ? 'form-control is-invalid'
                        : 'form-control'
                    }
                    id='emailAddress'
                    name='emailAddress'
                    data-testid='course-verify-information-email-address-field'
                    placeholder=''
                    type='email'
                  />
                  <ErrorMessage
                    name='emailAddress'
                    render={(msg) => (
                      <span
                        className='text-danger'
                        data-testid='course-verify-information-email-address-field-error'
                      >
                        {msg}
                      </span>
                    )}
                  />
                </div>
              </VerifyInfoFieldsRow>

              <VerifyInfoFieldsRow>
                <div className='form-group'>
                  <label htmlFor='mailingAddress'>Street Address</label>
                  <Field
                    as='textarea'
                    className='form-control'
                    id='mailingAddress'
                    name='mailingAddress'
                    data-testid='course-verify-information-mailing-address-field'
                    placeholder='Optional'
                  />
                  <ErrorMessage
                    name='mailingAddress'
                    render={(msg) => (
                      <span
                        className='text-danger'
                        data-testid='course-verify-information-mailing-address-field-error'
                      >
                        {msg}
                      </span>
                    )}
                  />
                </div>
              </VerifyInfoFieldsRow>

              <VerifyInfoFieldsRow>
                <div className='form-group'>
                  <label htmlFor='zipCode'>Zip Code</label>
                  <Field
                    className='form-control'
                    id='zipCode'
                    name='zipCode'
                    data-testid='course-verify-information-zip-code-field'
                    placeholder='Optional'
                    type='text'
                  />
                  <ErrorMessage
                    name='zipCode'
                    render={(msg) => (
                      <span
                        className='text-danger'
                        data-testid='course-verify-information-zip-code-field-error'
                      >
                        {msg}
                      </span>
                    )}
                  />
                </div>
                <div className='form-group'>
                  <label htmlFor='city'>City</label>
                  <Field
                    className='form-control'
                    id='city'
                    name='city'
                    data-testid='course-verify-information-city-field'
                    placeholder='Optional'
                    type='text'
                  />
                  <ErrorMessage
                    name='city'
                    render={(msg) => (
                      <span
                        className='text-danger'
                        data-testid='course-verify-information-city-field-error'
                      >
                        {msg}
                      </span>
                    )}
                  />
                </div>
                <Select
                  fieldStyling={
                    formik.errors.selectState
                      ? 'form-control is-invalid'
                      : 'form-control'
                  }
                  label='State'
                  name='selectState'
                  data-testid='course-verify-information-select-state-field'
                  options={dropdownStateOptions}
                  selectStyling='form-group'
                >
                  <ErrorMessage
                    name='selectState'
                    render={(msg) => (
                      <span
                        className='text-danger'
                        data-testid='course-verify-information-select-state-field-error'
                      >
                        {msg}
                      </span>
                    )}
                  />
                </Select>
              </VerifyInfoFieldsRow>

              {submitErrorMessage && (
                <span
                  className='text-danger'
                  data-testid='course-verify-information-submit-error-message'
                >
                  {submitErrorMessage}
                </span>
              )}
            </VerifyInfoFieldsWrapper>

            <TwoBtnsContainer>
              <Button
                variant='outline-secondary'
                onClick={handleBackButtonClick}
                data-testid='course-verify-information-back-button'
              >
                Back
              </Button>
              <Button
                type='submit'
                variant='primary'
                data-testid='course-verify-information-submit-button'
              >
                Submit
              </Button>
            </TwoBtnsContainer>
          </Form>
        )}
      </Formik>
    </>
  )
}

export default CoursesVerifyPrimaryForm
